import { useMemo, useState } from "react";
import {
  Building2,
  Search,
} from "lucide-react";

import type {
  PlatformChurchDetail,
  PlatformChurchSummary,
} from "@/api/platform";
import { WorkspaceDetails } from "@/components/platform/workspace/WorkspaceDetails";
import { WorkspaceList } from "@/components/platform/workspace/WorkspaceList";
import { WorkspaceShell } from "@/components/platform/workspace/WorkspaceShell";
import { ChurchOverviewPanel } from "@/features/platform/churches/components/ChurchOverviewPanel";
import {
  ChurchWorkspaceTabs,
  type ChurchWorkspaceTab,
} from "@/features/platform/churches/components/ChurchWorkspaceTabs";
import {
  usePlatformChurch,
  usePlatformChurches,
} from "@/features/platform/hooks/usePlatformChurches";
import { ChurchAdministratorsPanel } from "@/features/platform/churches/components/ChurchAdministratorsPanel";
import { ChurchInvitationsPanel } from "@/features/platform/churches/components/ChurchInvitationsPanel";
import { ChurchActivityPanel } from "@/features/platform/churches/components/ChurchActivityPanel";
import { ChurchOperationsPanel } from "@/features/platform/churches/components/ChurchOperationsPanel";

const PAGE_SIZE = 50;

export function PlatformChurchesPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [lifecycleStage, setLifecycleStage] = useState("");
  const [selectedChurchId, setSelectedChurchId] = useState<string>();
  const [activeTab, setActiveTab] =
    useState<ChurchWorkspaceTab>("overview");

  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: status || undefined,
      lifecycle_stage: lifecycleStage || undefined,
      offset: 0,
      limit: PAGE_SIZE,
    }),
    [search, status, lifecycleStage]
  );

  const churchesQuery = usePlatformChurches(filters);
  const churchQuery = usePlatformChurch(selectedChurchId);

  const churches = churchesQuery.data?.items ?? [];

  function handleChurchSelect(churchId: string) {
    setSelectedChurchId(churchId);
    setActiveTab("overview");
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-600">
          Platform Console
        </p>

        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
          Churches
        </h1>

        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
          Review church accounts, onboarding status, administrators,
          invitations, and pilot activity.
        </p>
      </header>

      <WorkspaceShell
        list={
          <WorkspaceList
            title="Church accounts"
            description={
              churchesQuery.data
                ? `${churchesQuery.data.total} church${
                    churchesQuery.data.total === 1 ? "" : "es"
                  }`
                : "Loading churches"
            }
            toolbar={
              <ChurchFilters
                search={search}
                status={status}
                lifecycleStage={lifecycleStage}
                onSearchChange={setSearch}
                onStatusChange={setStatus}
                onLifecycleStageChange={setLifecycleStage}
              />
            }
          >
            <ChurchList
              churches={churches}
              selectedChurchId={selectedChurchId}
              isLoading={churchesQuery.isLoading}
              isError={churchesQuery.isError}
              errorMessage={
                churchesQuery.error instanceof Error
                  ? churchesQuery.error.message
                  : "The church list could not be loaded."
              }
              onSelect={handleChurchSelect}
              onRetry={() => {
                void churchesQuery.refetch();
              }}
            />
          </WorkspaceList>
        }
        details={
          <WorkspaceDetails
            header={
              churchQuery.data ? (
                <>
                  <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50">
                        <Building2 className="h-6 w-6 text-indigo-600" />
                      </div>

                      <div>
                        <h2 className="text-2xl font-bold text-slate-950">
                          {churchQuery.data.church.name}
                        </h2>

                        <p className="mt-1 text-sm text-slate-500">
                          {churchQuery.data.church.slug}
                        </p>
                      </div>
                    </div>

                    <StatusBadge
                      value={churchQuery.data.church.status}
                    />
                  </div>

                  <ChurchWorkspaceTabs
                    value={activeTab}
                    onChange={setActiveTab}
                  />
                </>
              ) : undefined
            }
          >
            <ChurchDetails
              church={churchQuery.data}
              activeTab={activeTab}
              isLoading={churchQuery.isLoading}
              isError={churchQuery.isError}
              hasSelection={!!selectedChurchId}
              errorMessage={
                churchQuery.error instanceof Error
                  ? churchQuery.error.message
                  : "The church workspace could not be loaded."
              }
              onRetry={() => {
                void churchQuery.refetch();
              }}
              onTabChange={setActiveTab}
            />
          </WorkspaceDetails>
        }
      />
    </div>
  );
}

type ChurchFiltersProps = {
  search: string;
  status: string;
  lifecycleStage: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onLifecycleStageChange: (value: string) => void;
};

function ChurchFilters({
  search,
  status,
  lifecycleStage,
  onSearchChange,
  onStatusChange,
  onLifecycleStageChange,
}: ChurchFiltersProps) {
  return (
    <div className="space-y-3">
      <label className="relative block">
        <span className="sr-only">Search churches</span>

        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search churches"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <select
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        >
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="ARCHIVED">Archived</option>
        </select>

        <select
          value={lifecycleStage}
          onChange={(event) =>
            onLifecycleStageChange(event.target.value)
          }
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        >
          <option value="">All stages</option>
          <option value="ONBOARDING">Onboarding</option>
          <option value="PILOT">Pilot</option>
          <option value="CUSTOMER">Customer</option>
          <option value="OFFBOARDED">Offboarded</option>
        </select>
      </div>
    </div>
  );
}

type ChurchListProps = {
  churches: PlatformChurchSummary[];
  selectedChurchId?: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  onSelect: (churchId: string) => void;
  onRetry: () => void;
};

function ChurchList({
  churches,
  selectedChurchId,
  isLoading,
  isError,
  errorMessage,
  onSelect,
  onRetry,
}: ChurchListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-xl bg-slate-100"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-5">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <p className="text-sm font-semibold text-rose-950">
            Unable to load churches
          </p>

          <p className="mt-1 text-sm leading-6 text-rose-700">
            {errorMessage}
          </p>

          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-lg bg-rose-700 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-600"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (churches.length === 0) {
    return (
      <div className="flex min-h-72 flex-col items-center justify-center px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
          <Building2 className="h-6 w-6 text-slate-500" />
        </div>

        <p className="mt-4 text-sm font-semibold text-slate-900">
          No churches found
        </p>

        <p className="mt-1 text-sm leading-6 text-slate-500">
          Adjust the current search or filters.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-200">
      {churches.map((church) => {
        const isSelected = selectedChurchId === church.id;

        return (
          <button
            key={church.id}
            type="button"
            onClick={() => onSelect(church.id)}
            className={[
              "w-full px-5 py-4 text-left transition",
              isSelected
                ? "bg-indigo-50"
                : "bg-white hover:bg-slate-50",
            ].join(" ")}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-950">
                  {church.name}
                </p>

                <p className="mt-1 truncate text-xs text-slate-500">
                  {church.slug}
                </p>
              </div>

              <StatusBadge value={church.status} />
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-slate-500">
                {formatValue(church.lifecycle_stage)}
              </span>

              <span className="truncate text-xs text-slate-500">
                {church.administrator
                  ? formatAdministratorName(church.administrator)
                  : "No administrator"}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

type ChurchDetailsProps = {
  church?: PlatformChurchDetail;
  activeTab: ChurchWorkspaceTab;
  isLoading: boolean;
  isError: boolean;
  hasSelection: boolean;
  errorMessage: string;
  onRetry: () => void;
  onTabChange: (tab: ChurchWorkspaceTab) => void;
};

function ChurchDetails({
  church,
  activeTab,
  isLoading,
  isError,
  hasSelection,
  errorMessage,
  onRetry,
  onTabChange,
}: ChurchDetailsProps) {
  if (!hasSelection) {
    return (
      <div className="flex min-h-[34rem] flex-col items-center justify-center text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50">
          <Building2 className="h-7 w-7 text-indigo-600" />
        </div>

        <h2 className="mt-5 text-xl font-semibold text-slate-950">
          Select a church
        </h2>

        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
          Choose a church from the list to review its account,
          administrator, invitation, pilot request, and activity.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-72 rounded bg-slate-200" />
        <div className="h-4 w-96 max-w-full rounded bg-slate-100" />

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-32 rounded-2xl bg-slate-100"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !church) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-5">
        <p className="font-semibold text-rose-950">
          Unable to load the church workspace
        </p>

        <p className="mt-2 text-sm leading-6 text-rose-700">
          {errorMessage}
        </p>

        <button
          type="button"
          onClick={onRetry}
          className="mt-4 rounded-lg bg-rose-700 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600"
        >
          Try again
        </button>
      </div>
    );
  }

  switch (activeTab) {
    case "administrators":
       return (
            <ChurchAdministratorsPanel church={church} />
       );

    case "invitations":
       return <ChurchInvitationsPanel church={church} />;

    case "activity":
       return <ChurchActivityPanel church={church} />;

    case "operations":
      return <ChurchOperationsPanel church={church} onViewOverview={() => onTabChange("overview")} onViewInvitation={() => onTabChange("invitations")} />;

    case "overview":
    default:
      return <ChurchOverviewPanel church={church} />;
  }
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.toUpperCase();

  const className =
    normalized === "ACTIVE" ||
    normalized === "ACCEPTED" ||
    normalized === "CONVERTED"
      ? "bg-emerald-100 text-emerald-700"
      : normalized === "PENDING" ||
          normalized === "SENT" ||
          normalized === "SUBMITTED" ||
          normalized === "UNDER_REVIEW"
        ? "bg-amber-100 text-amber-700"
        : normalized === "SUSPENDED" ||
            normalized === "DECLINED" ||
            normalized === "REVOKED" ||
            normalized === "EXPIRED" ||
            normalized === "INACTIVE"
          ? "bg-rose-100 text-rose-700"
          : "bg-slate-100 text-slate-600";

  return (
    <span
      className={[
        "inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-semibold",
        className,
      ].join(" ")}
    >
      {formatValue(value)}
    </span>
  );
}

function formatValue(value?: string | null) {
  if (!value) {
    return "Not available";
  }

  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatAdministratorName(
  administrator: PlatformChurchSummary["administrator"] extends infer T
    ? NonNullable<T>
    : never
) {
  const fullName = [
    administrator.first_name,
    administrator.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || administrator.email;
}
